// GLSL for planets, clouds, atmosphere, rings, sun and sky.
// Render space: camera at the origin, axes = ecliptic J2000, metres.
// Every material includes the logdepthbuf chunks (renderer uses a logarithmic depth buffer).

export const commonGlsl = /* glsl */ `
  vec2 sphereUv(vec3 d) {
    return vec2(atan(d.y, d.x) * (0.5 / PI) + 0.5, asin(clamp(d.z, -1.0, 1.0)) / PI + 0.5);
  }
  // Equirectangular sampling without the mip seam at longitude ±180°.
  vec4 sampleSphere(sampler2D t, vec2 uv) {
    vec2 uv2 = vec2(fract(uv.x + 0.5), uv.y);
    vec2 dx = dFdx(uv), dy = dFdy(uv);
    vec2 dx2 = dFdx(uv2), dy2 = dFdy(uv2);
    if (abs(dx.x) > abs(dx2.x)) dx.x = dx2.x;
    if (abs(dy.x) > abs(dy2.x)) dy.x = dy2.x;
    return textureGrad(t, uv, dx, dy);
  }
`;

export const planetVertex = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  varying vec3 vDir;
  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;
  void main() {
    vDir = normalize(position);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vWorldNormal = normalize(mat3(modelMatrix) * position);
    gl_Position = projectionMatrix * viewMatrix * wp;
    #include <logdepthbuf_vertex>
  }
`;

export const planetFragment = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  uniform sampler2D map;
  uniform sampler2D nightMap;
  uniform sampler2D normalMap;
  uniform sampler2D specMap;
  uniform sampler2D cloudMap;
  uniform float hasNight, hasNormal, hasSpec, hasCloudShadow, hasAtmosphere;
  uniform vec3 tint;
  uniform vec3 sunPos;       // render space
  uniform mat3 bodyRot;      // body-fixed -> render space
  uniform float cloudShift;  // radians of cloud drift
  uniform float emissive;    // > 0 for the sun
  uniform float time;
  varying vec3 vDir;
  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;
  ${commonGlsl}

  void main() {
    #include <logdepthbuf_fragment>
    vec2 uv = sphereUv(vDir);
    vec3 albedo = sampleSphere(map, uv).rgb * tint;
    if (emissive > 0.0) {
      // Sun: limb darkening + granulation shimmer.
      vec3 V = normalize(-vWorldPos);
      float mu = clamp(dot(normalize(vWorldNormal), V), 0.0, 1.0);
      float limb = 1.0 - 0.6 * (1.0 - pow(mu, 0.5));
      gl_FragColor = vec4(albedo * emissive * limb * vec3(1.0, 0.86, 0.62), 1.0);
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
      return;
    }
    vec3 Ng = normalize(vWorldNormal);
    vec3 N = Ng;
    if (hasNormal > 0.5) {
      vec3 up = bodyRot * vec3(0.0, 0.0, 1.0);
      vec3 east = normalize(cross(up, Ng) + vec3(1e-6));
      vec3 north = cross(Ng, east);
      vec3 tn = sampleSphere(normalMap, uv).xyz * 2.0 - 1.0;
      N = normalize(east * tn.x * 1.6 + north * tn.y * 1.6 + Ng * tn.z);
    }
    vec3 L = normalize(sunPos - vWorldPos);
    vec3 V = normalize(-vWorldPos);
    float nl = dot(N, L);
    float ngl = dot(Ng, L);
    // Sunlight reddens near the terminator on bodies with air.
    vec3 sunColor = vec3(1.0);
    if (hasAtmosphere > 0.5) sunColor = mix(vec3(1.0, 0.42, 0.18), vec3(1.0), smoothstep(-0.02, 0.35, ngl));
    float shadow = 1.0;
    if (hasCloudShadow > 0.5) {
      vec3 cd = vDir;
      float c = cos(cloudShift), s = sin(cloudShift);
      cd.xy = mat2(c, s, -s, c) * cd.xy;
      float cloud = sampleSphere(cloudMap, sphereUv(cd)).r;
      shadow = 1.0 - 0.55 * cloud;
    }
    float diffuse = max(nl, 0.0) * smoothstep(-0.05, 0.05, ngl);
    vec3 color = albedo * sunColor * diffuse * shadow * 1.35;
    if (hasSpec > 0.5) {
      float water = sampleSphere(specMap, uv).r;
      vec3 H = normalize(L + V);
      float fres = 0.02 + 0.98 * pow(1.0 - max(dot(V, Ng), 0.0), 5.0);
      float spec = pow(max(dot(Ng, H), 0.0), 120.0) * 3.0 + pow(max(dot(Ng, H), 0.0), 12.0) * 0.08;
      color += water * sunColor * spec * (0.25 + fres) * smoothstep(0.0, 0.1, ngl) * shadow;
      color = mix(color, color * vec3(0.75, 0.85, 1.0), water * 0.4);
    }
    if (hasNight > 0.5) {
      vec3 lights = sampleSphere(nightMap, uv).rgb;
      float dark = 1.0 - smoothstep(-0.12, 0.06, ngl);
      color += lights * vec3(1.0, 0.8, 0.55) * dark * 1.6;
    }
    color += albedo * 0.0025; // faint starlight so the night side is never pure black
    gl_FragColor = vec4(color, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export const cloudFragment = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  uniform sampler2D cloudMap;
  uniform vec3 sunPos;
  uniform float cloudShift;
  uniform vec3 tint;
  uniform float opacity;
  varying vec3 vDir;
  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;
  ${commonGlsl}
  void main() {
    #include <logdepthbuf_fragment>
    vec3 cd = vDir;
    float c = cos(cloudShift), s = sin(cloudShift);
    cd.xy = mat2(c, s, -s, c) * cd.xy;
    vec4 tex = sampleSphere(cloudMap, sphereUv(cd));
    float a = tex.r * opacity;
    vec3 Ng = normalize(vWorldNormal);
    vec3 L = normalize(sunPos - vWorldPos);
    float ngl = dot(Ng, L);
    vec3 sunColor = mix(vec3(1.0, 0.45, 0.2), vec3(1.0), smoothstep(0.0, 0.3, ngl));
    float lit = smoothstep(-0.08, 0.2, ngl);
    vec3 col = tint * sunColor * (0.08 + lit * 1.25);
    gl_FragColor = vec4(col * a, a);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

// Single-scattering atmosphere (Rayleigh + Mie), ray-marched on a back-face shell.
// Math runs in planet-radius units to keep float32 precision near the surface.
export const atmosphereVertex = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  varying vec3 vWorldPos;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
    #include <logdepthbuf_vertex>
  }
`;

export const atmosphereFragment = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  uniform vec3 center;        // planet centre, render space (m)
  uniform float planetRadius; // m
  uniform float atmoRadius;   // m
  uniform vec3 sunDir;        // unit, from planet towards sun
  uniform vec3 betaR;         // 1/m
  uniform float betaM;
  uniform float scaleR;       // m
  uniform float scaleM;
  uniform float sunIntensity;
  varying vec3 vWorldPos;

  vec2 raySphere(vec3 ro, vec3 rd, float r) {
    float b = dot(ro, rd);
    float c = dot(ro, ro) - r * r;
    float h = b * b - c;
    if (h < 0.0) return vec2(1e9, -1e9);
    h = sqrt(h);
    return vec2(-b - h, -b + h);
  }

  void main() {
    #include <logdepthbuf_fragment>
    float R = planetRadius;
    vec3 ro = -center / R;               // camera relative to planet, in radii
    vec3 rd = normalize(vWorldPos);
    float Ra = atmoRadius / R;
    vec2 atm = raySphere(ro, rd, Ra);
    if (atm.x > atm.y || atm.y < 0.0) discard;
    vec2 ground = raySphere(ro, rd, 1.0);
    float t0 = max(atm.x, 0.0);
    float t1 = atm.y;
    bool hitGround = ground.x < ground.y && ground.x > 0.0;
    if (hitGround) t1 = min(t1, ground.x);
    const int STEPS = 16;
    const int LSTEPS = 6;
    float ds = (t1 - t0) / float(STEPS);
    float dsM = ds * R;
    vec3 sumR = vec3(0.0), sumM = vec3(0.0);
    float odR = 0.0, odM = 0.0;
    float mu = dot(rd, sunDir);
    float phaseR = 3.0 / (16.0 * PI) * (1.0 + mu * mu);
    float g = 0.76;
    float phaseM = 3.0 / (8.0 * PI) * ((1.0 - g * g) * (1.0 + mu * mu)) / ((2.0 + g * g) * pow(1.0 + g * g - 2.0 * g * mu, 1.5));
    for (int i = 0; i < STEPS; i++) {
      vec3 p = ro + rd * (t0 + ds * (float(i) + 0.5));
      float h = (length(p) - 1.0) * R;
      float hr = exp(-h / scaleR) * dsM;
      float hm = exp(-h / scaleM) * dsM;
      odR += hr; odM += hm;
      vec2 ls = raySphere(p, sunDir, Ra);
      float lds = ls.y / float(LSTEPS);
      float lodR = 0.0, lodM = 0.0;
      bool shadowed = false;
      for (int j = 0; j < LSTEPS; j++) {
        vec3 q = p + sunDir * (lds * (float(j) + 0.5));
        float lh = (length(q) - 1.0) * R;
        if (lh < 0.0) { shadowed = true; break; }
        lodR += exp(-lh / scaleR) * lds * R;
        lodM += exp(-lh / scaleM) * lds * R;
      }
      if (shadowed) continue;
      vec3 tau = betaR * (odR + lodR) + betaM * 1.1 * (odM + lodM);
      vec3 att = exp(-tau);
      sumR += att * hr;
      sumM += att * hm;
    }
    vec3 inscatter = sunIntensity * (sumR * betaR * phaseR + sumM * betaM * phaseM);
    vec3 trans = exp(-(betaR * odR + betaM * 1.1 * odM));
    float transAvg = dot(trans, vec3(0.3333));
    gl_FragColor = vec4(inscatter, transAvg);
  }
`;

export const ringFragment = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  uniform sampler2D ringMap;
  uniform float inner, outer;
  uniform vec3 center;       // planet centre (render space)
  uniform float planetRadius;
  uniform vec3 sunPos;
  varying vec3 vWorldPos;
  varying vec3 vLocal;
  void main() {
    #include <logdepthbuf_fragment>
    float r = length(vLocal.xy);
    float t = (r - inner) / (outer - inner);
    if (t < 0.0 || t > 1.0) discard;
    vec4 tex = texture2D(ringMap, vec2(t, 0.5));
    // Planet shadow: does the ray to the sun hit the planet sphere?
    vec3 L = normalize(sunPos - vWorldPos);
    vec3 oc = vWorldPos - center;
    float b = dot(oc, L);
    float c = dot(oc, oc) - planetRadius * planetRadius;
    float h = b * b - c;
    float lit = (h > 0.0 && -b - sqrt(h) > 0.0) ? 0.06 : 1.0;
    vec3 col = tex.rgb * lit * 1.1;
    gl_FragColor = vec4(col * tex.a, tex.a * 0.95);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export const ringVertex = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  varying vec3 vWorldPos;
  varying vec3 vLocal;
  void main() {
    vLocal = position;
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
    #include <logdepthbuf_vertex>
  }
`;

export const skyVertex = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = position;
    vec4 p = projectionMatrix * mat4(mat3(viewMatrix)) * vec4(position, 1.0);
    gl_Position = p.xyww; // at the far plane
  }
`;

export const skyFragment = /* glsl */ `
  #include <common>
  uniform sampler2D map;
  uniform mat3 eclToGal;
  uniform float brightness;
  varying vec3 vDir;
  ${commonGlsl}
  void main() {
    vec3 d = normalize(eclToGal * normalize(vDir));
    vec2 uv = sphereUv(d);
    uv.x = 1.0 - uv.x; // viewed from inside: galactic longitude increases to the left
    uv.x = fract(uv.x + 0.5);
    vec3 col = sampleSphere(map, uv).rgb;
    col = pow(col, vec3(1.35)) * brightness; // deepen the background, keep the stars
    gl_FragColor = vec4(col, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export const glowFragment = /* glsl */ `
  uniform vec3 color;
  uniform float intensity;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r = length(p);
    float core = exp(-r * r * 30.0);
    float halo = exp(-r * 5.0) * 0.35;
    float rays = pow(max(0.0, 1.0 - abs(p.x * p.y) * 60.0), 6.0) * exp(-r * 3.0) * 0.25;
    float a = (core + halo + rays) * smoothstep(1.0, 0.7, r);
    gl_FragColor = vec4(color * a * intensity, 1.0);
  }
`;

export const glowVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // Screen-facing billboard
    vec4 mv = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    vec2 scale = vec2(length(modelMatrix[0].xyz), length(modelMatrix[1].xyz));
    mv.xy += position.xy * scale;
    gl_Position = projectionMatrix * mv;
    gl_Position.z = 0.0; // always visible when in front; occlusion handled on the CPU
  }
`;
